/**
 * Thème clair / sombre / système (specs §6.8 et §8.1, archi §6.3).
 *
 * Point unique qui décide du thème actif. Trois choses en dépendent et doivent
 * rester d'accord : l'attribut `data-theme` sur `<html>`, la balise
 * `<meta name="theme-color">`, et la préférence conservée.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🔴 POURQUOI LA PRÉFÉRENCE N'EST PAS EN BASE — écart au §4.7
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Le §4.7 range le thème dans la table `setting`. Elle reste vide, et ce n'est
 * pas un oubli. Trois raisons, la troisième étant décisive :
 *
 *  1. **Le thème doit être posé avant le premier pixel.** Un aller-retour HTTP
 *     ne peut pas tenir cette promesse : on afficherait l'app en sombre, puis
 *     elle basculerait en clair une fois la réponse arrivée. Le seul moyen
 *     d'éviter ce clignotement est une lecture **synchrone** avant le rendu —
 *     donc `localStorage`, lu par le script en tête d'`index.html`.
 *  2. **L'écran de connexion en a besoin aussi**, et il n'y a alors aucune
 *     session pour lire un réglage de compte.
 *  3. 🔴 **Le mode « système » est par nature propre à l'appareil.** C'est le
 *     défaut, et c'est ce que les specs §6.8 demandent en premier. Un thème
 *     rangé côté compte serait poussé aux deux appareils à la fois et
 *     entrerait en conflit avec le réglage du système de chacun — l'iPhone
 *     passé en sombre le soir, l'ordinateur resté en clair. C'est le même
 *     raisonnement qu'aux retours de l'étape 8 pour le fuseau horaire : ce qui
 *     décrit **l'appareil qu'on tient** se lit sur cet appareil.
 *
 * `setting` garde donc tout son sens pour la V2 : les unités et préférences
 * d'affichage, elles, sont bien des réglages de compte.
 *
 * ⚠️ La logique de résolution est **écrite deux fois** : ici, et en une ligne
 * dans le script inline d'`index.html`, qui doit tourner avant le bundle. Le
 * doublon est réduit au strict minimum — la clé de stockage et l'ordre de
 * priorité — et `index.html` porte un avertissement qui renvoie ici. C'est le
 * même arbitrage que `lib/foodPreview.ts` à l'étape 6 : un doublon assumé,
 * documenté des deux côtés, parce que l'alternative est pire.
 */

/** Ce que l'utilisateur choisit. */
export type ThemeChoice = 'light' | 'dark' | 'system';

/** Ce qui est réellement appliqué — `<html data-theme>` ne vaut jamais autre chose. */
export type ResolvedTheme = 'light' | 'dark';

/**
 * ⚠️ Cette clé est répétée dans le script inline d'`index.html`. La changer ici
 * seul ferait perdre la préférence à chaque démarrage, sans erreur visible.
 */
export const THEME_STORAGE_KEY = 'nutri.theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function isChoice(value: unknown): value is ThemeChoice {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Préférence conservée, ou « système » par défaut (specs §8.1 : le sombre est
 * le défaut *attendu*, mais suivre l'appareil le donne déjà quand l'appareil
 * est en sombre — et respecte l'utilisateur quand il ne l'est pas).
 *
 * ⚠️ `localStorage` **lève** en navigation privée sur d'anciens Safari, et son
 * accès seul peut échouer. Un thème n'est pas une donnée : en cas d'échec on
 * retombe sur « système », jamais sur une erreur.
 */
export function storedChoice(): ThemeChoice {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isChoice(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

/** Ce que l'appareil demande, quand le choix est « système ». */
export function systemTheme(): ResolvedTheme {
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  return choice === 'system' ? systemTheme() : choice;
}

/**
 * 🔴 Applique le thème — **et met la balise `theme-color` à jour avec lui**.
 *
 * C'est le point que l'étape 9 avait explicitement laissé à l'étape 10 : la
 * balise valait `#0b0b0f` en dur, donc la barre d'état d'une PWA installée
 * serait restée noire sur un fond clair.
 *
 * ⚠️ **La valeur n'est pas écrite en dur ici non plus.** Elle est lue sur la
 * variable `--theme-color`, que `tokens.css` définit dans chaque thème : sans
 * cela il y aurait deux endroits à tenir d'accord, et le jour où le fond de
 * page change, la barre d'état garderait l'ancienne teinte sans que rien ne le
 * signale.
 *
 * ⚠️ Le manifeste, lui, **ne peut pas suivre** : `theme_color` y est une valeur
 * figée, lue à l'installation. Il garde donc le sombre, qui est le défaut des
 * specs §8.1 — c'est la couleur de l'écran de démarrage, pas celle de la barre
 * d'état en cours d'usage, et cette dernière est bien pilotée par la balise.
 */
export function applyTheme(choice: ThemeChoice): ResolvedTheme {
  const resolved = resolveTheme(choice);
  const root = document.documentElement;
  root.dataset.theme = resolved;

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    // Lue après avoir posé `data-theme` : c'est la valeur du thème qui vient
    // d'être appliqué, pas celle du précédent.
    const color = getComputedStyle(root).getPropertyValue('--theme-color').trim();
    if (color !== '') meta.setAttribute('content', color);
  }

  return resolved;
}

export function storeChoice(choice: ThemeChoice): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    /* Préférence non conservée : l'app reste utilisable, thème par défaut. */
  }
}

/**
 * Suit le réglage du système tant que le choix est « système ».
 *
 * ⚠️ `addEventListener('change')` et non `addListener` : la seconde forme est
 * dépréciée, mais surtout la première est la seule à accepter un retrait propre
 * — sans quoi chaque montée du composant de réglages empilerait un écouteur de
 * plus sur le même `MediaQueryList`.
 */
export function watchSystemTheme(onChange: () => void): () => void {
  const media = window.matchMedia(DARK_QUERY);
  media.addEventListener('change', onChange);
  return () => {
    media.removeEventListener('change', onChange);
  };
}
